// @flow
import * as React from 'react';

export type ButtonSize = 'xsmall' | 'small' | 'large';

export type ButtonAction = (props?: any) => React.Node;

export type ActionStatus = '' | 'completed' | 'error' | 'loading';

export type Actions = {
  ColumnsAction: ButtonAction,
  CopyAction: ButtonAction,
  ExportAction: ButtonAction,
  RefreshAction: ButtonAction,
  ResetAction: ButtonAction,
  SaveViewAction: ButtonAction,
  FieldViewerAction: ButtonAction,
  hasAllColumnsHidden: boolean,
  isOneRowSelected: boolean,
  noRowsSelected: boolean,
  hasDataToDisplay: boolean,
  loading: boolean,
  currentItem: any,
  getGlobalDisabledState: () => boolean,
  onOrderCreated: ActionStatus => typeof undefined,
  onOrderActivated: ActionStatus => typeof undefined,
  onOrderRemoved: ActionStatus => typeof undefined,
  onItemAdded: ActionStatus => typeof undefined,
  orderCreateStatus?: ActionStatus,
  orderActiveStatus?: ActionStatus,
  itemAddedStatus?: ActionStatus,
  moduleName: string,
};

// How we get to each of these states:
// 1. ''          : is the initial state
// 2. 'saved'     : user made a change and then `saved` it
// 3. 'unsaved'   : user made a change but still hasn't saved it
// 4. 'error'     : user made a change but there has been an error while trying saving the change
export type SaveStatus = '' | 'saved' | 'reset' | 'unsaved' | 'error';

// How we get to each of these states:
// 1. 'default'   : user has the default view, i.e. hasn't changed anything from the view we are sending to them
// 2. 'custom'    : user changed something in the grid and saved the changes
// 3. 'def_custom': user started with a 'default' state and then changed something
//   ~ This is useful when user clicks on `reset` before saving any change
//   ~ In this case we'll disable both buttons (`save` and `reset`) since technically the user is back to the `default` state
export type UserViewState = 'default' | 'custom' | 'def_custom';

export type ColumMap = {
  binding: string,
  header: string,
  visible?: boolean,
  id?: number,

  size?: number
};

export type ColumnsMap = Array<ColumMap>;

export type SortingDefinition = {
  sorting: Array<?{
    property: string,
    ascending: boolean,
  }>,
};

export type GroupPanelsDefinition = {
  groups: Array<string>,
};

export type AccountsItem = {
  Status?: string,
  AcctNo?: string,
  AcctName?: string,
  AcctNote?: string,
  Level?: string,
  Focus?: string,
  Type?: string,
  Growth?: string,
  Sup?: string,
  Address?: string,
  Wb1?: string,
  Wb1Phone?: string,
  Wb1Email?: string,
  Wb2?: string,
  Wb2Phone?: string,
  Wb2Email?: string,
  Wb3?: string,
  Wb3Phone?: string,
  Wb3Email?: string,
  Affil?: string,
  Bal?: number,
  Due?: number,
  DelNote?: string,
  Po?: string,
  Coop?: string,
  Lic?: string,
  Prem?: string,
  City?: string,
  County?: string,
  Last?: string,
  Rep?: string,
  Region?: string,
  MoboQty?: any,
  linkedMobos?: any,
};

// TODO: type the rest of the obj
export type InventoryItem = {
  Code: string,
  Brand: string,
  Vintage: string,
  Desc: string,
  Uom: number,
  Note: string,
  Account: AccountsItem,
  maxItemQty: {
    byOrderType: Array<{
      id?: string,
      Qty: number,
      Cases: number,
      Bottles: number,
    }>,
    ActionPerOrderType: string,
    PreviousAdd: {
      Qty: number,
      Cases: number,
      Bottles: number,
    },
  },
  Det: any,
  OrderTypeRaw: String
};

export type InvoiceItem = {
  Vintage?: string,
  Uom: number,
  DamNotes?: string,
  Code: string,
  MVendor?: string,
  Closure?: string,
  Type?: string,
  Varietal?: string,
  Org?: string,
  Bio?: string,
  Focus?: string,
  Country?: string,
  Region?: string,
  App?: string,
  RstOff?: string,
  RstOffNotes?: string,
  RstPrem?: string,
  RstAllo?: string,
  RstApp?: string,
  RstMax?: string,
  RstState?: string,
  RstSam?: string,
  RstBo?: string,
  RstMo?: string,
  Core?: string,
  FocusBm?: string,
  Upc?: string,
  ScoreWA?: string,
  ScoreWS?: string,
  ScoreVM?: string,
  ScoreOther?: string,
  QtyA?: number,
  QtyOh?: number,
  OnSo?: number,
  OnMo?: number,
  OnBo?: number,
  Level?: string,
  PriceC?: number,
  PriceB?: number,
  DiscountB?: number,
  DiscountC?: number,
  Description?: string,
  Discounts?: string,
  OnPo?: number,
  OnPoSort?: number,
  PoDate?: string,
  EtaDate?: string,
  Restrictions?: string,
};

export type MoboItem = {
  OrderNo: string,
  Type: string,
  ExpDate: string,
  AcctNo: string,
  Code: string,
  Description: string,
  Cases: number,
  Bottles: number,
  OnMOBO: number,
  Uom: number,
};

export type OrderItem = {
  id: number,
  OrderType: string,
  DateSaved: string,
  DateShip: string,
  AcctName: string,
  Cases: number,
  Bottles: number,
  Qty: number,
  Total: number,
  Mobo: string,
  Price: {
    value: number,
    rawValue: String,
    product: InventoryItem,
    mixedSizeItems: [InventoryItem],
    pricingTiers: [
      {
        tier: number,
        price: number,
      },
    ],
  },
  Uom: number,
};

export type GridItem = AccountsItem &
  InvoiceItem &
  MoboItem &
  OrderItem &
  InventoryItem;

export type FormatterArgs = {
  [string]: any,
  selectedState?: string
};

export type ReactObjRef<ElementType: React.ElementType> =
  {current: null | React.ElementRef<ElementType>}
